import { useEffect, useState, useRef } from 'react';
import { cn } from '../../lib/utils';

// Animated counter that counts up to a value
export const AnimatedCounter = ({ value, duration = 1000, prefix = '', suffix = '', className = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(0);

  useEffect(() => {
    startValueRef.current = displayValue;
    startTimeRef.current = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const current = startValueRef.current + (value - startValueRef.current) * easeOut;
      setDisplayValue(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatValue = (val) => {
    if (val >= 1000000) {
      return (val / 1000000).toFixed(1) + 'M';
    } else if (val >= 1000) {
      return (val / 1000).toFixed(1) + 'K';
    }
    return Math.round(val).toLocaleString('en-GB');
  };

  return (
    <span className={cn("tabular-nums", className)}>
      {prefix}{formatValue(displayValue)}{suffix}
    </span>
  );
};

// Animated progress bar
export const AnimatedProgress = ({ value, max = 100, color = 'bg-sky-500', className = '' }) => {
  const [width, setWidth] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setWidth((value / max) * 100);
    }, 100);
    return () => clearTimeout(timer);
  }, [value, max]);

  return (
    <div className={cn("h-2 bg-slate-200 rounded-full overflow-hidden", className)}>
      <div 
        className={cn("h-full rounded-full transition-all duration-1000 ease-out", color)}
        style={{ width: `${width}%` }}
      />
    </div>
  );
};

// Animated score ring
export const ScoreRing = ({ score, maxScore = 100, size = 120, strokeWidth = 8, color: _color = '#1e3a5f', label = '' }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedScore(score), 100);
    return () => clearTimeout(timer);
  }, [score]);

  const offset = circumference - (animatedScore / maxScore) * circumference;
  
  const getGrade = (s) => {
    if (s >= 85) return { grade: 'A+', color: '#10b981' };
    if (s >= 75) return { grade: 'A', color: '#22c55e' };
    if (s >= 65) return { grade: 'B+', color: '#84cc16' };
    if (s >= 55) return { grade: 'B', color: '#eab308' };
    if (s >= 45) return { grade: 'C+', color: '#f97316' };
    if (s >= 35) return { grade: 'C', color: '#ef4444' };
    return { grade: 'D', color: '#dc2626' };
  };

  const { grade, color: gradeColor } = getGrade(score);

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={gradeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color: gradeColor }}>{grade}</span>
        <span className="text-sm text-slate-500">{animatedScore}/{maxScore}</span>
        {label && <span className="text-xs text-slate-400 mt-1">{label}</span>}
      </div>
    </div>
  );
};

// Staggered list animation wrapper
export const StaggeredList = ({ children, className = '', staggerDelay = 50 }) => {
  return (
    <div className={className}>
      {Array.isArray(children) ? children.map((child, index) => (
        <div
          key={index}
          className="animate-fade-in-up"
          style={{ animationDelay: `${index * staggerDelay}ms` }}
        >
          {child}
        </div>
      )) : children}
    </div>
  );
};

// Pulse dot for live indicators
export const PulseDot = ({ color = 'bg-emerald-500', size = 'w-2 h-2' }) => (
  <span className="relative flex">
    <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", color)} />
    <span className={cn("relative inline-flex rounded-full", color, size)} />
  </span>
);

// Animated gradient border card
export const GradientBorderCard = ({ children, className = '', gradient = 'from-sky-400 via-violet-500 to-fuchsia-500' }) => (
  <div className={cn("relative p-[2px] rounded-xl overflow-hidden", className)}>
    <div className={cn("absolute inset-0 bg-gradient-to-r animate-gradient-x", gradient)} />
    <div className="relative bg-white rounded-[10px] p-4">
      {children}
    </div>
  </div>
);

// Shimmer loading placeholder
export const Shimmer = ({ className = 'h-4 w-full' }) => (
  <div className={cn("bg-slate-200 rounded animate-shimmer bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%]", className)} />
);

// Sparkle effect component
export const Sparkles = ({ children, className = '' }) => {
  const [sparkles, setSparkles] = useState([]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      const newSparkle = {
        id: Math.random(),
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 10 + 5,
      };
      setSparkles(prev => [...prev.slice(-5), newSparkle]);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={cn("relative", className)}>
      {sparkles.map(sparkle => (
        <span
          key={sparkle.id}
          className="absolute animate-sparkle pointer-events-none"
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`,
            fontSize: `${sparkle.size}px`,
          }}
        >
          ✨
        </span>
      ))}
      {children}
    </div>
  );
};

// Activity pulse animation
export const ActivityPulse = ({ active = false, className = '' }) => (
  <div className={cn("relative", className)}>
    {active && (
      <>
        <div className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-25" />
        <div className="absolute inset-0 rounded-full bg-emerald-400 animate-pulse opacity-50" />
      </>
    )}
    <div className={cn("relative rounded-full", active ? "bg-emerald-500" : "bg-slate-300")} />
  </div>
);

// Floating action hint
export const FloatingHint = ({ text, show = true, position = 'bottom' }) => {
  if (!show) return null;
  
  const positionClasses = {
    top: 'bottom-full mb-2',
    bottom: 'top-full mt-2',
    left: 'right-full mr-2',
    right: 'left-full ml-2',
  };

  return (
    <div className={cn(
      "absolute z-50 px-3 py-1.5 text-xs font-medium text-white bg-slate-900 rounded-lg shadow-lg animate-bounce-subtle",
      positionClasses[position]
    )}>
      {text}
      <div className={cn(
        "absolute w-2 h-2 bg-slate-900 transform rotate-45",
        position === 'bottom' && "-top-1 left-1/2 -translate-x-1/2",
        position === 'top' && "-bottom-1 left-1/2 -translate-x-1/2",
      )} />
    </div>
  );
};

// Confetti explosion effect
export const useConfetti = () => {
  const [isActive, setIsActive] = useState(false);
  
  const trigger = () => {
    setIsActive(true);
    setTimeout(() => setIsActive(false), 3000);
  };

  const ConfettiComponent = () => {
    if (!isActive) return null;
    
    const colors = ['#1e3a5f', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6'];
    const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 0.5,
      rotation: Math.random() * 360,
    }));

    return (
      <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
        {confettiPieces.map(piece => (
          <div
            key={piece.id}
            className="absolute animate-confetti"
            style={{
              left: `${piece.x}%`,
              backgroundColor: piece.color,
              animationDelay: `${piece.delay}s`,
              transform: `rotate(${piece.rotation}deg)`,
              width: '10px',
              height: '10px',
              borderRadius: '2px',
            }}
          />
        ))}
      </div>
    );
  };

  return { trigger, ConfettiComponent };
};
